import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  CreditCard,
  Users,
  Heart,
  ImageIcon,
  Clipboard,
  Clock,
  Mic,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";

interface PatientDemographicsProps {
  patient: {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    phone: string;
    email: string | null;
    address_line1: string;
    address_line2: string | null;
    city: string;
    state: string;
    zip_code: string;
    insurance_type: string;
    medicare_id: string | null;
    medicaid_id: string | null;
    ssn_last_four: string | null;
    group_number: string | null;
    plan_type: string | null;
    payer_name: string | null;
    claims_address: string | null;
    payer_phone: string | null;
    edi_payer_id: string | null;
    rx_bin: string | null;
    rx_pcn: string | null;
    rx_group: string | null;
    rx_id: string | null;
    effective_date: string | null;
    copay_amount: string | null;
    deductible_info: string | null;
    network_tier: string | null;
    medicare_part_a: boolean | null;
    medicare_part_b: boolean | null;
    medicare_part_c: boolean | null;
    medicare_part_d: boolean | null;
    emergency_contact_name: string;
    emergency_contact_phone: string;
    emergency_contact_relationship: string;
    living_situation: string | null;
    social_support_level: string | null;
    preferred_communication_style: string | null;
    insurance_card_front_url: string | null;
    insurance_card_back_url: string | null;
    user_id?: string;
  };
  patientId?: string;
}

export function PatientDemographics({ patient, patientId }: PatientDemographicsProps) {
  const resolvedPatientId = patientId || patient.user_id;

  const { data: recentNotes } = useQuery({
    queryKey: ["patient-recent-notes", resolvedPatientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_notes")
        .select("*")
        .eq("patient_id", resolvedPatientId!)
        .order("created_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return data || [];
    },
    enabled: !!resolvedPatientId,
  });
  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Full Name</p>
              <p className="font-medium">{patient.first_name} {patient.last_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Date of Birth</p>
              <p className="font-medium">
                {format(new Date(patient.date_of_birth), "MMMM d, yyyy")}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Phone</p>
              <p className="font-medium flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                {patient.phone}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                {patient.email || "Not provided"}
              </p>
            </div>
          </div>
          {patient.ssn_last_four && (
            <div>
              <p className="text-sm text-muted-foreground">SSN (Last 4)</p>
              <p className="font-medium">***-**-{patient.ssn_last_four}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Address */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Address
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-medium">{patient.address_line1}</p>
          {patient.address_line2 && <p className="font-medium">{patient.address_line2}</p>}
          <p className="font-medium">
            {patient.city}, {patient.state} {patient.zip_code}
          </p>
        </CardContent>
      </Card>

      {/* Insurance Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Insurance Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Insurance Type</p>
              <Badge variant="outline" className="capitalize mt-1">
                {patient.insurance_type?.replace("_", " ")}
              </Badge>
            </div>
            {patient.payer_name && (
              <div>
                <p className="text-sm text-muted-foreground">Payer Name</p>
                <p className="font-medium">{patient.payer_name}</p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {patient.medicare_id && (
              <div>
                <p className="text-sm text-muted-foreground">Medicare ID</p>
                <p className="font-medium font-mono">{patient.medicare_id}</p>
              </div>
            )}
            {patient.medicaid_id && (
              <div>
                <p className="text-sm text-muted-foreground">Medicaid ID</p>
                <p className="font-medium font-mono">{patient.medicaid_id}</p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {patient.group_number && (
              <div>
                <p className="text-sm text-muted-foreground">Group ID</p>
                <p className="font-medium font-mono">{patient.group_number}</p>
              </div>
            )}
            {patient.plan_type && (
              <div>
                <p className="text-sm text-muted-foreground">Plan Type</p>
                <p className="font-medium">{patient.plan_type}</p>
              </div>
            )}
          </div>
          {patient.effective_date && (
            <div>
              <p className="text-sm text-muted-foreground">Effective Date</p>
              <p className="font-medium">{patient.effective_date}</p>
            </div>
          )}
          {(patient.medicare_part_a || patient.medicare_part_b || patient.medicare_part_c || patient.medicare_part_d) && (
            <div>
              <p className="text-sm text-muted-foreground mb-1">Medicare Coverage</p>
              <div className="flex flex-wrap gap-1">
                {patient.medicare_part_a && <Badge variant="secondary">Part A</Badge>}
                {patient.medicare_part_b && <Badge variant="secondary">Part B</Badge>}
                {patient.medicare_part_c && <Badge variant="secondary">Part C</Badge>}
                {patient.medicare_part_d && <Badge variant="secondary">Part D</Badge>}
              </div>
            </div>
          )}
          {(patient.copay_amount || patient.deductible_info || patient.network_tier) && (
            <div className="grid grid-cols-2 gap-4">
              {patient.copay_amount && (
                <div>
                  <p className="text-sm text-muted-foreground">Copay</p>
                  <p className="font-medium">{patient.copay_amount}</p>
                </div>
              )}
              {patient.deductible_info && (
                <div>
                  <p className="text-sm text-muted-foreground">Deductible</p>
                  <p className="font-medium">{patient.deductible_info}</p>
                </div>
              )}
              {patient.network_tier && (
                <div>
                  <p className="text-sm text-muted-foreground">Network Tier</p>
                  <p className="font-medium">{patient.network_tier}</p>
                </div>
              )}
            </div>
          )}
          {(patient.payer_phone || patient.edi_payer_id) && (
            <div className="grid grid-cols-2 gap-4">
              {patient.payer_phone && (
                <div>
                  <p className="text-sm text-muted-foreground">Payer Phone</p>
                  <p className="font-medium">{patient.payer_phone}</p>
                </div>
              )}
              {patient.edi_payer_id && (
                <div>
                  <p className="text-sm text-muted-foreground">EDI Payer ID</p>
                  <p className="font-medium font-mono">{patient.edi_payer_id}</p>
                </div>
              )}
            </div>
          )}
          {patient.claims_address && (
            <div>
              <p className="text-sm text-muted-foreground">Claims Address</p>
              <p className="font-medium text-sm">{patient.claims_address}</p>
            </div>
          )}
          {(patient.rx_bin || patient.rx_pcn || patient.rx_group || patient.rx_id) && (
            <div>
              <p className="text-sm text-muted-foreground mb-2">Pharmacy Benefits</p>
              <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-muted/30">
                {patient.rx_bin && (
                  <div>
                    <p className="text-xs text-muted-foreground">Rx BIN</p>
                    <p className="font-medium font-mono text-sm">{patient.rx_bin}</p>
                  </div>
                )}
                {patient.rx_pcn && (
                  <div>
                    <p className="text-xs text-muted-foreground">Rx PCN</p>
                    <p className="font-medium font-mono text-sm">{patient.rx_pcn}</p>
                  </div>
                )}
                {patient.rx_group && (
                  <div>
                    <p className="text-xs text-muted-foreground">Rx Group</p>
                    <p className="font-medium font-mono text-sm">{patient.rx_group}</p>
                  </div>
                )}
                {patient.rx_id && (
                  <div>
                    <p className="text-xs text-muted-foreground">Rx ID</p>
                    <p className="font-medium font-mono text-sm">{patient.rx_id}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          {(patient.insurance_card_front_url || patient.insurance_card_back_url) && (
            <div>
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                <ImageIcon className="h-4 w-4" />
                Insurance Card Images
              </p>
              <div className="flex flex-wrap gap-4">
                {patient.insurance_card_front_url && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Front</p>
                    <a href={patient.insurance_card_front_url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={patient.insurance_card_front_url}
                        alt="Insurance card front"
                        className="w-48 h-28 rounded-lg object-cover border hover:ring-2 hover:ring-primary transition-all cursor-pointer"
                      />
                    </a>
                  </div>
                )}
                {patient.insurance_card_back_url && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Back</p>
                    <a href={patient.insurance_card_back_url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={patient.insurance_card_back_url}
                        alt="Insurance card back"
                        className="w-48 h-28 rounded-lg object-cover border hover:ring-2 hover:ring-primary transition-all cursor-pointer"
                      />
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Emergency Contact
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground">Name</p>
            <p className="font-medium">{patient.emergency_contact_name}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Relationship</p>
              <p className="font-medium capitalize">{patient.emergency_contact_relationship}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Phone</p>
              <p className="font-medium">{patient.emergency_contact_phone}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Social Information */}
      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5" />
            Social & Communication
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Living Situation</p>
              <p className="font-medium capitalize">
                {patient.living_situation?.replace("_", " ") || "Not specified"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Social Support Level</p>
              <p className="font-medium capitalize">
                {patient.social_support_level?.replace("_", " ") || "Not specified"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Preferred Communication</p>
              <p className="font-medium capitalize">
                {patient.preferred_communication_style || "Not specified"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Notes */}
      {recentNotes && recentNotes.length > 0 && (
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clipboard className="h-5 w-5" />
              Recent Notes
            </CardTitle>
            <CardDescription>Latest clinical notes for this patient</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentNotes.map((note) => {
              const services = Array.isArray(note.services) ? note.services : [];
              return (
                <div key={note.id} className="p-3 rounded-lg border bg-muted/30 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      {format(new Date(note.created_at), "MMM d, yyyy 'at' h:mm a")}
                    </div>
                    <div className="flex items-center gap-2">
                      {note.voice_memo_url && (
                        <Badge variant="outline" className="gap-1 text-xs">
                          <Mic className="h-3 w-3" />
                          Voice
                        </Badge>
                      )}
                      {note.billing_synced ? (
                        <Badge variant="default" className="text-xs">Synced</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Pending</Badge>
                      )}
                    </div>
                  </div>
                  {note.note_text && (
                    <p className="text-sm text-foreground whitespace-pre-wrap line-clamp-3">
                      {note.note_text}
                    </p>
                  )}
                  {services.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {services.map((svc: any, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {svc.label}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
